<template>
  <div
    class="bg-white fadeIn shadow-lg relative overflow-hidden border border-gray-200 text-sm max-w-4xl mx-auto"
  >
    <div class="folded-corner"></div>
    <div class="folded-corner-border"></div>

    <div v-if="!loading">
      <div v-if="referralData">
        <!-- Core Referral Text -->
        <div class="border-b border-gray-200">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-gray-600">Core Referral Text</h2>
              <Button v-if="isGuide" @click="toggleCoreReferralEdit" variant="outline" size="sm">
                <Edit class="w-4 h-4 mr-2" />
                {{ isEditingCoreReferral ? 'Cancel' : 'Edit Text' }}
              </Button>
            </div>
          </div>

          <div class="p-6">
            <div v-if="!isEditingCoreReferral" class="text-gray-900">
              <span
                v-if="referralData?.referral_package?.core_referral_text"
                class="whitespace-pre-wrap"
                >{{ referralData?.referral_package?.core_referral_text }}</span
              >
              <span v-else class="text-gray-500 italic">No core referral text provided</span>
            </div>

            <div v-else-if="isGuide && isEditingCoreReferral" class="space-y-3">
              <Textarea
                v-model="editedCoreReferralText"
                placeholder="Core referral text"
                rows="5"
                class="w-full"
              />
              <div class="flex gap-2 justify-end">
                <Button @click="saveCoreReferralText" :disabled="isSaving" size="sm">
                  {{ isSaving ? 'Saving...' : 'Save' }}
                </Button>
                <Button @click="cancelCoreReferralEdit" variant="outline" size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Info with Edit Button -->
        <div class="border-b border-gray-200">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-gray-600">Contact Information</h2>
              <a
                :href="`https://outtalent.com/profile/${referralData?.user_details?.username}/edit`"
                target="_blank"
                class="text-xs font-medium border-[1px] border-gray-200 rounded-md hover:bg-gray-50 bg-white flex items-center gap-2 py-2 px-4"
              >
                <Edit class="w-4 h-4 mr-2" />
                Edit Profile
              </a>
            </div>
          </div>
          <table class="w-full">
            <tbody>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6 w-56">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <User class="w-4 h-4 text-gray-500" />
                    Full Name
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.full_name || 'Not provided' }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Phone class="w-4 h-4 text-gray-500" />
                    Phone
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.phone_number || 'Not provided' }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Link class="w-4 h-4 text-gray-500" />
                    LinkedIn
                  </div>
                </td>
                <td class="py-3 px-6">
                  <a
                    v-if="referralData?.user_profile?.linkedin_url"
                    :href="referralData.user_profile.linkedin_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-cosmicPurple hover:underline"
                  >
                    {{ referralData.user_profile.linkedin_url }}
                  </a>
                  <span v-else class="text-gray-900">Not provided</span>
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <CalendarIcon class="w-4 h-4 text-gray-500" />
                    Calendly
                  </div>
                </td>
                <td class="py-3 px-6">
                  <a
                    v-if="referralData?.user_profile?.calendly_url"
                    :href="referralData.user_profile.calendly_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-cosmicPurple hover:underline"
                  >
                    {{ referralData.user_profile.calendly_url }}
                  </a>
                  <span v-else class="text-gray-900">Not provided</span>
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <MessageCircle class="w-4 h-4 text-gray-500" />
                    Telegram
                  </div>
                </td>
                <td class="py-3 px-6">
                  <a
                    v-if="referralData?.user_profile?.telegram_handle"
                    :href="`https://t.me/${referralData.user_profile.telegram_handle.replace('@', '')}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-cosmicPurple hover:underline"
                  >
                    @{{ referralData.user_profile.telegram_handle }}
                  </a>
                  <span v-else class="text-gray-900">Not provided</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Location Info -->
        <div class="border-b border-gray-200">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h2 class="font-semibold text-gray-600">Location</h2>
          </div>
          <table class="w-full">
            <tbody>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6 w-56">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <MapPin class="w-4 h-4 text-gray-500" />
                    Country of Residence
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{
                    referralData?.user_profile?.country_of_residence_details?.country_name ||
                    'Not provided'
                  }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <MapPin class="w-4 h-4 text-gray-500" />
                    City of Residence
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.city_of_residence || 'Not provided' }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Globe class="w-4 h-4 text-gray-500" />
                    Country of Origin
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{
                    referralData?.user_profile?.country_of_origin_details?.country_name ||
                    'Not provided'
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Work Experience -->
        <div class="border-b border-gray-200">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h2 class="font-semibold text-gray-600">Work Experience</h2>
          </div>
          <table class="w-full">
            <tbody>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6 w-56">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Briefcase class="w-4 h-4 text-gray-500" />
                    Current Role
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.current_role || 'Not provided' }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Briefcase class="w-4 h-4 text-gray-500" />
                    Current Company
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.current_company || 'Not provided' }}
                </td>
              </tr>
              <tr class="border-b border-gray-100 last:border-0">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <Calendar class="w-4 h-4 text-gray-500" />
                    Years of Experience
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{
                    new Date().getFullYear() - referralData?.user_profile?.professional_start_year
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Education -->
        <div class="border-b border-gray-200">
          <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h2 class="font-semibold text-gray-600">Education</h2>
          </div>
          <table class="w-full">
            <tbody>
              <tr class="border-b border-gray-100">
                <td class="py-3 px-6 w-56">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <GraduationCap class="w-4 h-4 text-gray-500" />
                    Bachelor's Degree
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.bachelor_degree === 'YES' ? 'Yes' : 'No' }}
                </td>
              </tr>
              <template v-if="referralData?.user_profile?.bachelor_degree === 'YES'">
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <Building class="w-4 h-4 text-gray-500" />
                      University
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.bachelor_degree_university || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      Discipline
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.bachelor_degree_discipline || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      Start Year 
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.bachelor_degree_start_year || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      End Year 
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.bachelor_degree_end_year || 'Not provided' }}
                  </td>
                </tr>
              </template>
              <tr class="border-b border-gray-100">
                <td class="py-3 px-6">
                  <div class="font-medium flex items-center gap-2 text-gray-700">
                    <GraduationCap class="w-4 h-4 text-gray-500" />
                    Master's Degree
                  </div>
                </td>
                <td class="py-3 px-6 text-gray-900">
                  {{ referralData?.user_profile?.master_degree === 'YES' ? 'Yes' : 'No' }}
                </td>
              </tr>
              <template v-if="referralData?.user_profile?.master_degree === 'YES'">
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <Building class="w-4 h-4 text-gray-500" />
                      University
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.master_degree_university || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100 last:border-0">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      Discipline
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.master_degree_discipline || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      Start Year 
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.master_degree_start_year || 'Not provided' }}
                  </td>
                </tr>
                <tr class="border-b border-gray-100">
                  <td class="py-3 px-6 pl-12">
                    <div class="font-medium flex items-center gap-2 text-gray-700">
                      <School class="w-4 h-4 text-gray-500" />
                      End Year 
                    </div>
                  </td>
                  <td class="py-3 px-6 text-gray-900">
                    {{ referralData?.user_profile?.master_degree_end_year || 'Not provided' }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="text-center py-8">
        <span class="text-gray-500">No referral package data available.</span>
      </div>
    </div>

    <div v-else class="text-center py-8">
      <span class="text-gray-500">Loading your referral package...</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useToast } from '@/components/ui/toast/use-toast'
import { useApplicantProfileStore } from '@/stores/applicantProfile'
import {
  User,
  Phone,
  Link,
  CalendarIcon,
  MessageCircle,
  MapPin,
  Globe,
  Briefcase,
  Calendar,
  GraduationCap,
  Building,
  School,
  Edit
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/composables/useAuth'

const { toast } = useToast()
const applicantProfileStore = useApplicantProfileStore()

const { isGuide } = useAuth()

const isEditingCoreReferral = ref(false)
const editedCoreReferralText = ref('')
const isSaving = ref(false)

const referralData = computed(() => applicantProfileStore.applicantProfile)
const loading = computed(() => applicantProfileStore.loading)

const toggleCoreReferralEdit = () => {
  if (!isEditingCoreReferral.value) {
    editedCoreReferralText.value = referralData.value?.referral_package?.core_referral_text || ''
  }
  isEditingCoreReferral.value = !isEditingCoreReferral.value
}

const cancelCoreReferralEdit = () => {
  isEditingCoreReferral.value = false
  editedCoreReferralText.value = ''
}

const saveReferralPackageField = async (
  fieldName: string,
  value: string,
  isEditingRef: { value: boolean },
  fieldDisplayName: string
) => {
  if (!referralData.value) return

  isSaving.value = true
  try {
    await applicantProfileStore.updateReferralPackage({ 
      [fieldName]: value 
    })
    const userId = referralData.value.user_details?.id
    if (userId) {
      await applicantProfileStore.fetchApplicantProfileById(userId)
    }
    isEditingRef.value = false
    toast({
      title: 'Success',
      description: `${fieldDisplayName} updated successfully.`
    })
  } catch (err) {
    console.error(`Failed to update ${fieldDisplayName.toLowerCase()}:`, err)
    toast({
      variant: 'destructive',
      title: 'Error',
      description: `Failed to update ${fieldDisplayName.toLowerCase()}. Please try again.`
    })
  } finally {
    isSaving.value = false
  }
}

const saveCoreReferralText = () => 
  saveReferralPackageField('core_referral_text', editedCoreReferralText.value, isEditingCoreReferral, 'Core referral text')
</script>

<style scoped>
.folded-corner {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(225deg, transparent 50%, #f3f4f6 50%);
  box-shadow: -2px 2px 3px rgba(0, 0, 0, 0.05);
  pointer-events: none;
  z-index: 2;
}

.folded-corner::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(225deg, transparent 50%, #e5e7eb 50%);
  transform: translate(1px, -1px);
  z-index: -1;
}

.folded-corner-border {
  position: absolute;
  top: 0;
  right: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(225deg, transparent 48%, #e5e7eb 48%, #e5e7eb 50%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}
</style>
